import type { FC } from 'react';
import { useId, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { noDataAvailableStyles } from 'src/theme/sharedStyles';
import type { DashboardWidgetProps } from 'src/types/dashboard';
import { apiPaths } from 'src/services/api/apiPaths';
import WidgetFrame from 'src/components/dashboard/DashboardPage/components/WidgetFrame';
import VerticalBarChart from 'src/components/followUp/common/BaseWidgets/VerticalBarChart';
import type { AggregateList } from 'src/components/followUp/common/types';
import WidgetFooterLegend from 'src/components/followUp/common/WidgetFooterLegend/WidgetFooterLegend';
import WidgetHeaderWithPill from 'src/components/followUp/common/WidgetHeaderWithPill/WidgetHeaderWithPill';
import useGenerateWidgetEndpoint from 'src/components/followUp/hooks/useGenerateWidgetEndpoint';
import useWidgetQuery from 'src/components/followUp/hooks/useWidgetQuery';

import { useDataMapper, useExportOptions } from './hooks';

const ResponseFunnelWidget: FC<DashboardWidgetProps> = ({ config }) => {
  const ariaId = useId();
  const dataMapper = useDataMapper();
  const { t } = useTranslation();

  const endpoint = useGenerateWidgetEndpoint(
    apiPaths.WIDGET_DATA.RESPONSE_FUNNEL,
    config.id,
  );

  const footerLegendLabels = [
    {
      color: 'transparent',
      name: t('common:noDataAvailable'),
      customBackgroundSx: noDataAvailableStyles.noDataAvailable,
    },
  ];

  const { data, isFetching, errorMsg, refetch, queryFilters, comparisonData } =
    useWidgetQuery<AggregateList>(endpoint);

  const bars = useMemo(() => dataMapper(data), [data, dataMapper]);

  const comparisonBars = useMemo(
    () => dataMapper(comparisonData),
    [comparisonData, dataMapper],
  );

  const exportOptions = useExportOptions({
    bars,
    comparisonBars,
    widgetName: config.name,
    queryFilters,
  });

  return (
    <WidgetFrame
      ariaId={ariaId}
      refetch={refetch}
      loading={isFetching}
      error={errorMsg}
      exportOptions={exportOptions}
      header={<WidgetHeaderWithPill title={config.name} ariaId={ariaId} />}
      footer={<WidgetFooterLegend labels={footerLegendLabels} />}
    >
      {bars && (
        <VerticalBarChart
          bars={bars}
          comparisonBars={comparisonBars}
          isRelative
        />
      )}
    </WidgetFrame>
  );
};

export default ResponseFunnelWidget;
